import { useState } from "react";
import { Search, Loader2, Check, X, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useCart } from "@/lib/CartContext";

export default function DomainSearch() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const { openWith } = useCart();

  const search = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/domains/check", { query });
      setResults(data.results || []);
    } catch (err) {
      setError("Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative section-pad overflow-hidden" data-testid="domain-search-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-4">02 · Domains</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1]">
              Find your <span className="italic text-gradient">perfect domain</span>.
            </h2>
            <p className="mt-5 text-[15px] text-[color:var(--text-muted)] max-w-[520px] leading-relaxed">
              Every great business starts with a name. Search across popular TLDs — we'll
              handle registration, DNS, and business email setup.
            </p>
          </div>
        </div>

        <form onSubmit={search} className="relative rounded-2xl border border-[color:var(--border-strong)] bg-[color:var(--surface)] p-2 flex items-center gap-2" data-testid="domain-search-form">
          <div className="pl-4 text-[color:var(--text-muted)]"><Search size={18} /></div>
          <input
            data-testid="domain-search-input"
            className="flex-1 bg-transparent outline-none py-4 text-[16px] placeholder:text-[color:var(--text-muted)]"
            placeholder="Search your business name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="submit"
            data-testid="domain-search-submit"
            className="h-11 px-5 rounded-xl bg-[color:var(--text)] text-[color:var(--bg)] font-semibold text-[13px] inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : "Search"}
          </button>
        </form>

        {error && <div className="mt-4 text-sm text-red-500" data-testid="domain-error">{error}</div>}

        <AnimatePresence mode="wait">
          {results.length > 0 && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2"
              data-testid="domain-results"
            >
              {results.map((r, i) => (
                <motion.div
                  key={r.domain}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="flex items-center justify-between rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-5 py-4 hover:border-[color:var(--border-strong)] transition-colors"
                  data-testid={`domain-result-${r.domain}`}
                >
                  <div className="flex items-center gap-4">
                    <StatusPill status={r.status} />
                    <span className="font-mono text-[14.5px]">{r.domain}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {r.price_gbp != null && (
                      <span className="text-[13px] text-[color:var(--text-muted)]">£{r.price_gbp}/yr</span>
                    )}
                    {r.status !== "taken" && (
                      <button
                        onClick={() => openWith({ domain: r })}
                        className="h-8 px-3 rounded-lg bg-brand-primary text-white text-[12px] font-semibold hover:opacity-90 transition-opacity"
                        data-testid={`domain-cta-${r.domain}`}
                      >
                        Get started
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function StatusPill({ status }) {
  if (status === "available")
    return <span className="inline-flex items-center gap-1.5 text-[11.5px] font-mono uppercase tracking-widest text-emerald-500"><Check size={12} /> Available</span>;
  if (status === "taken")
    return <span className="inline-flex items-center gap-1.5 text-[11.5px] font-mono uppercase tracking-widest text-red-500"><X size={12} /> Taken</span>;
  return <span className="inline-flex items-center gap-1.5 text-[11.5px] font-mono uppercase tracking-widest text-brand-accent"><Sparkles size={12} /> Premium</span>;
}
